<template>
  <header>
    <!-- Standard navbar otherwise -->
    <v-app-bar
      app
      class="pricing-toolbar elevation-6"
      :class="{ warning: notUsingLocalDatabase, ...configurableNavbarColor }"
    >
      <v-toolbar-items>
        <v-btn exact text to="/" class="subheading font-weight-bold home-link" depressed>
          <!-- make the (optional) client logo invisible until loaded -->
          <v-img
            id="client-logo"
            src="/organisation-assets/logo.png"
            style="visibility: invisible"
            @load="logoLoaded"
          />
          <v-img :src="pricingLogo" width="12rem" />
        </v-btn>
      </v-toolbar-items>
      <v-spacer />
      <v-toolbar-items v-for="(route, index) in routes" :key="index">
        <template v-if="route.meta && route.meta.featureFlag">
          <feature-toggle :toggle="route.meta.featureFlag">
            <main-navbar-tab
              :route="route"
              :is-work-package-selected="isWorkPackageSelected"
              :is-navbar-highlighted="isNavbarHighlighted"
              :index="index"
            />
          </feature-toggle>
        </template>
        <main-navbar-tab
          v-else
          :route="route"
          :is-work-package-selected="isWorkPackageSelected"
          :is-navbar-highlighted="isNavbarHighlighted"
          :index="index"
        />
      </v-toolbar-items>
      <v-spacer />
      <v-toolbar-items>
        <v-btn
          v-if="isAdmin"
          id="navbar-settings"
          text
          exact
          class="text-none subheading settings-link"
          to="/admin"
          depressed
        >
          <span><v-icon v-once color="primary">mdi-cog</v-icon></span>
        </v-btn>
      </v-toolbar-items>
      <v-toolbar-items>
        <v-menu offset-y>
          <template v-slot:activator="{ on }">
            <v-btn text class="user-menu" v-on="on" @click="isExpanded = !isExpanded">
              <v-avatar class="avatar" height="4rem" width="4rem" color="primary" size="36">
                <span class="white--text headline">{{ profileNameAndInitials.initials }}</span>
              </v-avatar>
              <span class="name-text link" :class="{ 'link-dark': isNavbarHighlighted }">{{
                profileNameAndInitials.fullName
              }}</span>
              <main-expand-button :is-expanded="isExpanded" :background="isNavbarHighlighted" />
            </v-btn>
          </template>

          <v-list>
            <v-list-item>
              <v-list-item-title>
                <v-btn
                  id="navbar-logout"
                  text
                  class="text-none subheading nav-link"
                  depressed
                  @click="onLogout()"
                >
                  <span class="align-bottom">{{ startCase($t('actions.logout')) }}</span>
                </v-btn>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-toolbar-items>
    </v-app-bar>
    <v-app-bar v-if="shouldShowWorkpackageSelection" app class="workpackage-selection">
      <v-layout row align-center class="flex-nowrap">
        <v-flex :xs="isCompetitorSimulationEnabled ? 1 : 2" description>
          <tooltip :value="selectedWorkpackage.description" position="bottom">
            <a class="text-no-wrap" @click="goToWorkpackages">
              <v-icon v-once color="primary" class="font-weight-bold"> chevron_left </v-icon>
              <span v-if="dateHasPassed(selectedWorkpackage.goLiveDate)">
                ({{ $t('workPackages.archived') }})
              </span>
              {{ selectedWorkpackage.description | truncate(workpackageTruncationLength) }}
            </a>
          </tooltip>
        </v-flex>
        <v-flex :xs="isCompetitorSimulationEnabled ? 1 : 2">
          {{ selectedWorkpackage.storeFormat }}
        </v-flex>
        <v-flex xs2 class="text-xs-right" dates>
          <feature-toggle toggle="enableReferenceDate">
            {{ $t('workPackages.referenceDate') }}:
            <span class="font-weight-bold">
              {{ referenceDate }}
            </span>
            / <br />
          </feature-toggle>
          {{ $t('workPackages.completionDate') }}:
          <span class="font-weight-bold">
            {{ completionDate }}
          </span>
          / {{ $t('workPackages.goLiveDate') }}:
          <span class="font-weight-bold">
            {{ formatLocalisedDate(selectedWorkpackage.goLiveDate) }}
          </span>
        </v-flex>

        <template v-if="isCompetitorSimulationEnabled">
          <v-flex xs1>
            <tooltip
              :value="$t('gridView.engineRunWithSimulatedCompetitorPrices')"
              position="bottom"
            >
              <v-switch
                id="competitor-simulation-switch"
                v-model="selectedWorkpackage.useSimulatedCompetitorPrices"
                :loading="isUpdatingUseSimulatedCompetitorPrices"
                :disabled="isUpdatingUseSimulatedCompetitorPrices"
                :label="$t('gridView.engineSimulated')"
                primary
                dense
                class="pt-0"
                @change="toggleCompetitorSimulation($event)"
              />
            </tooltip>
          </v-flex>
        </template>
        <v-flex :xs="isCompetitorSimulationEnabled ? 3 : 3">
          <div class="actions mr-2">
            <navbar-engine-controls />
          </div>
        </v-flex>

        <v-flex :xs="isCompetitorSimulationEnabled ? 4 : 3">
          <div class="d-flex align-center">
            <feature-toggle :toggle="enableWorkpackageSettingsModal">
              <workpackage-settings-modal :workpackage="selectedWorkpackage" />
            </feature-toggle>
            <alerts v-if="displayAlerts()" />
          </div>
        </v-flex>
      </v-layout>
    </v-app-bar>
  </header>
</template>

<script>
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import { includes, isEmpty, startCase } from 'lodash';
import dateHasPassed from '../store/utils/date-checking';
import { wholesale as wholesalePageIndex } from '@enums/page-index';
import workpackageTypes from '@enums/workpackage-types';
import {
  navbarColorHighlightAll,
  navbarColorHighlightOw,
  enableCompetitorSimulation,
  enableWorkpackageSettingsModal,
} from '@enums/feature-flags';
import featureFlagsMixin from '@/js/mixins/featureFlags';

const pricingLogo = require('../../img/logo-rtls-pricing.png');

export default {
  mixins: [featureFlagsMixin],

  data() {
    return {
      sidebar: false,
      isExpanded: false,
      isUpdatingUseSimulatedCompetitorPrices: false,
      dateHasPassed,
      pricingLogo,
      enableWorkpackageSettingsModal,
    };
  },
  computed: {
    ...mapState('workpackages', ['selectedWorkpackage', 'workpackageTruncationLength']),
    ...mapState('clientConfig', ['notUsingLocalDatabase', 'toggleLogic']),
    ...mapState('gridView', ['activeTableFilters', 'calculatingScenarioResults']),
    ...mapGetters('context', [
      'hasPermission',
      'isAdmin',
      'isWholesaleManager',
      'isWholesaleAnalyst',
      'isOWUser',
      'profileNameAndInitials',
    ]),
    ...mapState('filters', ['selectedLevelEntryKey']),

    isCompetitorSimulationEnabled() {
      return this.isFeatureFlagEnabled(enableCompetitorSimulation);
    },

    routes() {
      return this.$router.options.routes
        .filter(route => {
          if (route.index === wholesalePageIndex) {
            if (!this.canAccessWholesale) return false;
            if (this.selectedWorkpackage.type !== workpackageTypes.master) return false;
          }
          if (route.meta && route.meta.requiresPermission) {
            if (!this.hasPermission(route.meta.requiresPermission)) {
              return false;
            }
          }
          return !route.omitFromNav;
        })
        .sort((a, b) => {
          if (Number.isFinite(a.index) && Number.isFinite(b.index)) return a.index - b.index;
          if (Number.isFinite(a.index)) return -1;
          if (Number.isFinite(b.index)) return 1;

          return a.name > b.name ? -1 : 0;
        });
    },

    canAccessWholesale() {
      return this.isWholesaleManager || this.isWholesaleAnalyst;
    },

    isWorkPackageSelected() {
      return !isEmpty(this.selectedWorkpackage);
    },

    completionDate() {
      return this.selectedWorkpackage.completionDate
        ? this.formatLocalisedDate(this.selectedWorkpackage.completionDate)
        : '-';
    },

    referenceDate() {
      return this.selectedWorkpackage.referenceDate
        ? this.formatLocalisedDate(this.selectedWorkpackage.referenceDate)
        : '-';
    },

    isNavbarHighlighted() {
      return (
        this.isOWUser &&
        !isEmpty(
          this.toggleLogic[navbarColorHighlightOw] || this.toggleLogic[navbarColorHighlightAll]
        )
      );
    },

    configurableNavbarColor() {
      return this.isOWUser && this.toggleLogic[navbarColorHighlightOw]
        ? { [this.toggleLogic[navbarColorHighlightOw]]: true }
        : { [this.toggleLogic[navbarColorHighlightAll]]: true };
    },

    isUnitManagerView() {
      return this.$route.path.startsWith('/pricing/impact-view');
    },

    // Show the fixed workpackage-selection bar on all routes except home and reporting.
    shouldShowWorkpackageSelection() {
      const excludedRoutes = ['home', 'reportingConfig', 'reportingView'];
      return this.isWorkPackageSelected && !includes(excludedRoutes, this.$route.name);
    },
  },
  methods: {
    startCase,
    ...mapActions('context', ['logout', 'resetState']),
    ...mapActions('gridView', ['fetchAggregations', 'getAlertCounts', 'removeActiveTableFilter']),
    ...mapActions('scenarioMetadata', ['getAggregatedScenarios']),
    ...mapActions('workpackages', ['updateWorkpackage']),
    ...mapMutations('notifications', ['addNotification']),

    async toggleCompetitorSimulation(useSimulatedCompetitorPrices) {
      this.isUpdatingUseSimulatedCompetitorPrices = true;
      try {
        await this.updateWorkpackage({
          workpackage: this.selectedWorkpackage,
          updates: { useSimulatedCompetitorPrices },
        });
        this.addNotification({
          id: `simulated-competitor-engine-toggle-${Date.now()}`,
          jobStatus: 'finished',
          baseTranslation: 'notifications.simulatedCompetitorEngineToggle',
        });
      } catch (e) {
        this.addNotification({
          id: `simulated-competitor-engine-toggle-${Date.now()}`,
          jobStatus: 'failed',
          baseTranslation: 'notifications.simulatedCompetitorEngineToggle',
        });
        throw e;
      } finally {
        this.isUpdatingUseSimulatedCompetitorPrices = false;
      }
    },

    onLogout() {
      return this.logout().then(
        redirectUrl => {
          this.$router.push(redirectUrl);
          this.resetState();
        },
        () => {
          // eslint-disable-next-line no-console
          console.log('Logout error!');
        }
      );
    },

    goToWorkpackages() {
      this.$router.push({ path: '/home' });
    },

    displayAlerts() {
      // display alerts on every page except wholesale
      return !this.$route.path.includes('/wholesale');
    },

    logoLoaded() {
      // if the client logo exists, make it visible and style it
      document.getElementById('client-logo').style.visibility = 'visible';
      document.getElementById('client-logo').style.width = '2.5rem';
      document.getElementById('client-logo').style['margin-right'] = '1rem';
    },
  },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';
@import '@style/mixins/_nav-tab.scss';
@import '@style/components/_hierarchy-item-shared.scss';

.actions {
  @include hierarchy-item-actions-panel();
  .v-btn {
    height: 2.4rem;
    font-size: 1.25rem;
  }
}

.pricing-toolbar {
  background-color: $pricing-white;
  z-index: 100;
}

.home-link {
  color: $pricing-light-blue;
}
.home-link:hover {
  text-decoration: none;
}

.link-dark {
  color: $pricing-white;
}

.nav-link {
  @include nav-link-overrides();

  &.nav-link-dark {
    color: $pricing-white;
  }
}

.active-link {
  @include active-link-overrides();
}

.v-btn {
  @include v-btn-overrides();
}

.settings-link {
  color: $pricing-primary;
}

.align-bottom {
  margin-bottom: -1rem;
}

.pricing-toolbar.v-app-bar--fixed.warning {
  background-color: $pricing-orange-dark !important;
}

.workpackage-selection {
  margin-top: 64px !important;
  background-color: white;
  width: 100%;
  z-index: 90;
  color: #4a4a4a;
  font-size: 1.4rem;

  .description {
    font-weight: 600;
  }

  .dates {
    color: #37424a;
    font-size: 1.2rem;
  }
}

.avatar {
  margin-right: 1rem;
}

#navbar-logout {
  padding-bottom: 1rem;
  margin-left: 3rem;
}

::v-deep .v-input--switch {
  max-height: 20px !important;
}

@media (max-width: 1650px) {
  ::v-deep label.v-label[for='competitor-simulation-switch'] {
    display: none !important;
  }
}
</style>
